<template>
  <div class="contenedor-tabla">
    <LoadingOverlay
      :visible="clonando !== null || descontinuando || completandoEtapa || (props.modoEvidencias && loading)"
      :message="clonando !== null ? 'Clonando modelo...' : descontinuando ? 'Descontinuando versión...' : completandoEtapa ? 'Completando etapa...' : 'Cargando modelos...'"
    />

    <div class="filtro-botones">
      <button
        v-for="t in opcionesFiltro"
        :key="t.label"
        :class="['btn-filtro', filtroTipo === t.value ? 'activo' : '']"
        @click="filtroTipo = t.value; filtroPrograma = ''; filtroEstado = ''; filtroFecha = ''; filtroBusqueda = ''"
      >
        {{ t.label }}
      </button>
    </div>

    <!-- Filtro de programas -->
    <div class="filtro-select" v-if="filtroTipo === 'Programas'">
      <label for="programaSelect">Programa:</label>
      <select
        id="programaSelect"
        v-model="filtroPrograma"
        class="form-control"
      >
        <option value="">Todos</option>
        <option
          v-for="p in opcionesProgramas"
          :key="p"
          :value="p"
        >
          {{ p }}
        </option>
      </select>
    </div>

    <!-- Filtros adicionales -->
    <div class="filtros-secundarios">
      <!-- Filtro por estado -->
      <div class="filtro-grupo">
        <label class="filtro-label" for="filtroEstado">Estado:</label>
        <select id="filtroEstado" v-model="filtroEstado" class="select-filtro">
          <option value="">Todos</option>
          <option v-for="e in opcionesEstado" :key="e.value" :value="e.value">{{ e.label }}</option>
        </select>
      </div>

      <!-- Filtro por fecha -->
      <div class="filtro-grupo">
        <label class="filtro-label" for="filtroFecha">Fecha:</label>
        <select id="filtroFecha" v-model="filtroFecha" class="select-filtro">
          <option value="">Todas</option>
          <option v-for="f in opcionesFechaFiltro" :key="f.value" :value="f.value">{{ f.label }}</option>
        </select>
      </div>

      <!-- Búsqueda por nombre -->
      <div class="filtro-grupo">
        <span class="filtro-label">Buscar:</span>
        <input
          type="text"
          v-model="filtroBusqueda"
          placeholder="Nombre del modelo..."
          class="input-busqueda"
        />
      </div>

      <!-- Toggle solo en modo indicadores -->
      <div v-if="props.modoIndicadores" class="filtro-grupo">
        <label class="toggle-label">
          <input type="checkbox" v-model="mostrarVersionesProcesosActivos" class="toggle-check" />
          Mostrar versiones en proceso activo
        </label>
      </div>
    </div>

    <!-- Mensaje de carga-->
    <div v-if="loading && !props.modoEvidencias" class="loading">Cargando modelos...</div>

    <!-- Cards: modo normal = modelos, otros modos = versiones -->
    <div class="cards-grid" v-if="!loading && (modoNormal ? modelosParaCardsNormal.length > 0 : versionesFiltradas.length > 0)">

      <!-- Modo normal: una card por modelo -->
      <template v-if="modoNormal">
        <div
          v-for="m in modelosPaginados"
          :key="m.id_modelo"
          :class="['modelo-card', cardSeleccionada === m.id_modelo ? 'card-activa' : '']"
          @click="toggleCard(m.id_modelo!)"
        >
          <div class="estado-badge">
            <span :class="['estado', estadoClass(m.estado)]">{{ m.estado }}</span>
          </div>

          <h3 class="card-titulo">{{ m.nombre }}</h3>

          <p class="card-info">Fecha: {{ formatFecha(m.fecha_creacion) }}</p>

          <p v-if="m.vigencia" class="card-info">Vigencia: {{ m.vigencia }}</p>

          <p v-if="m.acuerdo" class="card-info">{{ m.acuerdo }}</p>

          <p class="card-info">Tipo: {{ tipoDeModeloById(m.id_tipo_modelo)?.tipo ?? 'Sin tipo' }}</p>

          <div v-if="cardSeleccionada === m.id_modelo" class="card-acciones">
            <button class="btn guardar" @click.stop="accederModelo(m)">Acceder</button>
            <button
              v-if="auth.tienePermiso('versiones.crear')"
              class="btn guardar"
              @click.stop="abrirModalVersionarModelo(m)"
              :disabled="versionando !== null"
            >
              {{ versionando !== null ? 'Versionando...' : 'Versionar' }}
            </button>
            <button
              v-if="auth.tienePermiso('modelos.crear')"
              class="btn guardar"
              @click.stop="abrirModalClonarModelo(m)"
              :disabled="clonando !== null"
            >
              {{ clonando !== null ? 'Clonando...' : 'Clonar' }}
            </button>
            <button
              v-if="auth.tienePermiso('versiones.aprobar')"
              class="btn peligro"
              :disabled="descontinuando"
              @click.stop="abrirConfirmarDescontinuarModelo(m)"
            >Descontinuar</button>
          </div>
        </div>
      </template>

      <!-- Otros modos: una card por versión -->
      <template v-else>
        <div
          v-for="v in versionesPaginadas"
          :key="v.id_version"
          :class="['modelo-card', cardSeleccionada === v.id_version ? 'card-activa' : '']"
          @click="toggleCard(v.id_version!)"
        >
          <div class="estado-badge">
            <span :class="['estado', estadoClass(props.modoIndicadores ? v.estado : v.modeloEstado)]">
              {{ props.modoIndicadores ? v.estado : v.modeloEstado }}
            </span>
          </div>

          <h3 class="card-titulo">{{ v.modeloNombre }}</h3>

          <p class="card-info">Fecha: {{ formatFecha(v.fecha_creacion) }}</p>

          <p class="card-info">Tipo: {{ tipoDeModelo(v)?.tipo ?? 'Sin tipo' }}</p>

          <div
            v-if="versionesConProceso.has(v.id_version!)"
            class="badge-proceso-activo"
          >
            En proceso · {{ etiquetaEstadoProceso(versionesEstadoProceso[v.id_version!]) }}
          </div>

          <div v-if="cardSeleccionada === v.id_version" class="card-acciones">
            <template v-if="props.modoIndicadores">
              <button
                v-if="auth.tienePermiso('indicadores.crear')"
                class="btn guardar"
                @click.stop="agregarIndicadoresVersion(v)"
              >Agregar Indicadores</button>
              <button
                v-if="auth.tienePermiso('versiones.crear')"
                class="btn guardar"
                @click.stop="abrirEditarVersion(v)"
              >Editar</button>
              <button
                v-if="auth.tienePermiso('versiones.crear')"
                class="btn guardar"
                @click.stop="clonarVersion(v)"
              >Clonar</button>
            </template>

            <template v-if="props.modoCalificar">
              <button
                v-if="auth.tienePermiso('calificacion.calificar')"
                class="btn guardar"
                @click.stop="calificarInstrumentosVersion(v)"
              >Calificar Indicadores</button>
              <button
                class="btn completar"
                :disabled="completandoEtapa"
                @click.stop="completarEtapa(v)"
              >{{ completandoEtapa ? 'Completando...' : 'Completar Calificación' }}</button>
            </template>

            <template v-if="props.modoEvidencias">
              <button
                v-if="auth.tienePermiso('evidencias.subir')"
                class="btn guardar"
                @click.stop="subirEvidenciasVersion(v)"
              >Subir evidencias</button>
              <button
                class="btn completar"
                :disabled="completandoEtapa"
                @click.stop="completarEtapa(v)"
              >{{ completandoEtapa ? 'Completando...' : 'Completar' }}</button>
            </template>

            <template v-if="props.modoAsignarEvaluadores">
              <button
                v-if="auth.tienePermiso('procesos.asignar')"
                class="btn guardar"
                @click.stop="navegarEvaluacionExpertos(v, 'AsignarEvaluadores')"
              >Evaluadores</button>
              <button
                v-if="auth.tienePermiso('calificacion.ver')"
                class="btn guardar"
                @click.stop="navegarEvaluacionExpertos(v, 'ResultadosConsolidados')"
              >Resultados</button>
              <button
                v-if="auth.tienePermiso('calificacion.ver')"
                class="btn guardar"
                @click.stop="navegarEvaluacionExpertos(v, 'AuditoriaEvaluaciones')"
              >Auditoría</button>
            </template>

            <template v-if="props.modoEvaluacionExpertos">
              <button
                v-if="auth.tienePermiso('expertos.gestionar')"
                class="btn guardar"
                @click.stop="navegarEvaluacionExpertos(v, 'EvaluacionExpertos')"
              >Evaluar Modelo</button>
            </template>

            <button
              v-if="auth.tienePermiso('versiones.aprobar') && v.estado !== 'descontinuado'"
              class="btn peligro"
              :disabled="descontinuando"
              @click.stop="abrirConfirmarDescontinuarVersion(v)"
            >Descontinuar</button>
          </div>
        </div>
      </template>
    </div>

    <!-- Controles de paginación -->
    <div v-if="!loading && totalPaginas > 1" class="paginacion">
      <button
        class="btn-pag"
        :disabled="paginaActual === 1"
        @click="paginaActual--"
      >Anterior</button>
      <span class="pag-info">{{ paginaActual }} de {{ totalPaginas }}</span>
      <button
        class="btn-pag"
        :disabled="paginaActual === totalPaginas"
        @click="paginaActual++"
      >Siguiente</button>
    </div>

    <div v-else-if="!loading && error" class="error">
      <p>Error al cargar modelos: {{ error }}</p>
      <button class="btn guardar" @click="cargar">Reintentar</button>
    </div>

    <p v-else-if="!loading && !(modoNormal ? modelosParaCardsNormal.length : versionesFiltradas.length)">No hay Modelos creados aún.</p>
    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
    <VersionarModal
      :visible="modalVersionarVisible"
      @confirmar="confirmarVersionar"
      @cancelar="modalVersionarVisible = false; versionParaVersionar = null; modeloParaVersionar = null"
    />
    <ClonarModeloModal
      :visible="modalClonarVisible"
      :idModelo="modalClonarIdModelo"
      :idVersionOrigen="modalClonarIdVersionOrigen"
      @aceptar="confirmarClonarModelo"
      @cancelar="modalClonarVisible = false; versionParaClonar = null; modeloParaClonar = null"
    />

    <!-- Modal confirmación descontinuar -->
    <div v-if="confirmarDescontinuarVisible" class="ev-overlay" @click.self="cerrarConfirmarDescontinuar">
      <div class="ev-panel">
        <h4 class="ev-titulo">Confirmar acción</h4>
        <p class="dc-aviso">
          Esta acción es irreversible. Para confirmar, escribe
          <strong>descontinuar</strong> en el campo y presiona Aceptar.
        </p>
        <p v-if="confirmarDescontinuarNombre" class="dc-objetivo">{{ confirmarDescontinuarNombre }}</p>
        <div class="ev-campo">
          <input
            v-model="confirmarDescontinuarTexto"
            type="text"
            class="ev-input"
            placeholder="Escribe: descontinuar"
            @keyup.enter="ejecutarDescontinuar"
          />
        </div>
        <div class="ev-botones">
          <button class="btn salir" @click="cerrarConfirmarDescontinuar">Cancelar</button>
          <button
            class="btn guardar"
            :disabled="descontinuando || confirmarDescontinuarTexto.trim().toLowerCase() !== 'descontinuar'"
            @click="ejecutarDescontinuar"
          >{{ descontinuando ? 'Procesando...' : 'Aceptar' }}</button>
        </div>
      </div>
    </div>

    <!-- Modal editar nombre de versión -->
    <div v-if="editarVersionVisible" class="ev-overlay" @click.self="editarVersionVisible = false">
      <div class="ev-panel">
        <h4 class="ev-titulo">Editar nombre de versión</h4>
        <div class="ev-campo">
          <label class="ev-label" for="ev-input-nombre">Nombre</label>
          <input
            id="ev-input-nombre"
            v-model="nombreEdicion"
            type="text"
            class="ev-input"
            placeholder="Nombre de la versión"
            @keyup.enter="guardarEdicionVersion"
          />
        </div>
        <div class="ev-botones">
          <button class="btn salir" :disabled="guardandoEdicion" @click="editarVersionVisible = false">Cancelar</button>
          <button class="btn guardar" :disabled="guardandoEdicion || !nombreEdicion.trim()" @click="guardarEdicionVersion">
            {{ guardandoEdicion ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Listado de modelos con su estado y acciones.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import useAutenticar from '@/store/IngresoAutenticar'
import { useAsignacionActual } from '@/composables/useAsignacionActual'
import useModelo from '@/store/GestionModelo'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionTipoModelos } from '@/store/GestionTipoModelo'
import { useGestionProgramas } from '@/store/GestionPrograma'
import { useGestionCampus } from '@/store/GestionCampus'
import { useGestionProcesos } from '@/store/GestionProcesoAutoevaluacion'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import type Version from '@/interfaces/Versiones'
import type { Programa } from '@/interfaces/Programa'
import LoadingOverlay from '@/components/LoadingOverlay.vue' 
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import ClonarModeloModal from '@/components/ClonarModeloModal.vue'
import VersionarModal from '@/components/VersionarModal.vue'

interface ModeloConVersiones extends EstructuraModelo {
  versions: Version[]
}

interface VersionExtendida extends Version {
  modeloNombre: string
  id_tipo_modelo: number
  modeloEstado: string
}

const props = defineProps<{ modoIndicadores?: boolean, modoCalificar?: boolean, modoEvidencias?: boolean, modoAsignarEvaluadores?: boolean, modoEvaluacionExpertos?: boolean }>()
const auth = useAutenticar()
const { necesitaFiltro, idsVersiones, tieneAsignacionProceso } = useAsignacionActual()
const clonando = ref<number | null>(null)
const clonarEnProceso = ref(false) 
const descontinuando = ref(false) 
const cardSeleccionada = ref<number | null>(null)
const router = useRouter()
const modeloStore = useModelo()
const tipoModeloStore = useGestionTipoModelos()
const programaStore = useGestionProgramas()
const campusStore = useGestionCampus()
const procesosStore = useGestionProcesos()

// Set de id_version que tienen al menos un proceso activo (no finalizado)
const versionesConProceso = ref<Set<number>>(new Set())
// Set de id_version que están en al menos un proceso Y todos son finalizado
const versionesTodosFinalizados = ref<Set<number>>(new Set())
// Mapa id_version → estado e id del proceso activo
const versionesEstadoProceso = ref<Record<number, string>>({})
const versionesIdProceso     = ref<Record<number, number>>({})
const completandoEtapa = ref(false)
const modelos = ref<ModeloConVersiones[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const versionesStore = useGestionVersiones()

// Modal clonar modelo
const modalClonarVisible = ref(false)
const versionParaClonar = ref<VersionExtendida | null>(null)
const modeloParaClonar = ref<ModeloConVersiones | null>(null)

// Modal versionar
const modalVersionarVisible = ref(false)
const versionParaVersionar = ref<VersionExtendida | null>(null)
const modeloParaVersionar = ref<ModeloConVersiones | null>(null)
const versionando = ref<number | null>(null)

// Filtros
const filtroTipo = ref('')
const filtroPrograma = ref('')
const filtroEstado = ref('')
const filtroFecha = ref('')
const filtroBusqueda = ref('')

const mostrarVersionesProcesosActivos = ref(false)

const opcionesEstado = [
  { label: 'Completado', value: 'completado' },
  { label: 'Aprobado', value: 'aprobado' },
  { label: 'Edición', value: 'edicion' },
  { label: 'Descontinuado', value: 'descontinuado' }
]

const opcionesFechaFiltro = [
  { label: 'Hoy', value: 'hoy' },
  { label: 'Última semana', value: 'semana' },
  { label: 'Este mes', value: 'mes' }
]

const formatterFecha = new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })

// Función para parsear fechas correctamente (maneja formato YYYY-MM-DD del backend)
function parseFecha(fecha?: string | null): Date | null {
  if (!fecha) return null
  const soloFecha = fecha.substring(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(soloFecha)) {
    const [yyyy, mm, dd] = soloFecha.split('-').map(Number)
    return new Date(yyyy, mm - 1, dd)
  }
  const d = new Date(fecha)
  return isNaN(d.getTime()) ? null : d
}

// Opciones de filtro únicas por nombre de tipo
const opcionesFiltro = computed(() => {
  const seen = new Set<string>()
  const result: { label: string; value: string }[] = [{ label: 'Todos', value: '' }]

  for (const t of tipoModeloStore.tipos) {
    if (!seen.has(t.tipo)) {
      seen.add(t.tipo)
      result.push({ label: t.tipo, value: t.tipo })
    }
  }

  return result
})

// Opciones de programas únicos desde el store de programas
const opcionesProgramas = computed(() => {
  return programaStore.programas.map(p => p.nombre)
})

// Filtrado solo por tipo y programa
const modelosFiltrados = computed(() => {
  let base = modelos.value

  if (filtroTipo.value) {
    base = base.filter(m => {
      const tipo = tipoModeloStore.tipos.find(t => t.id_tipo_modelo === m.id_tipo_modelo)
      return tipo?.tipo === filtroTipo.value
    })
  }

  if (filtroTipo.value === 'Programas' && filtroPrograma.value) {
    // id_programa ahora vive en la versión (C-03)
    base = base.filter(m =>
      (m.versions ?? []).some((v: any) => {
        const programa = programaStore.programas.find(p => p.id_programa === v.id_programa)
        return programa?.nombre === filtroPrograma.value
      })
    )
  }

  return base
})

const modoNormal = computed(() =>
  !props.modoIndicadores && !props.modoCalificar && !props.modoEvidencias &&
  !props.modoAsignarEvaluadores && !props.modoEvaluacionExpertos
)

const ordenEstadosModelo: Record<string, number> = { completado: 1, aprobado: 2, edicion: 3, descontinuado: 4 }

const modelosParaCardsNormal = computed<ModeloConVersiones[]>(() => {
  let base = modelosFiltrados.value

  if (filtroEstado.value) {
    base = base.filter(m => m.estado === filtroEstado.value)
  }

  if (filtroFecha.value) {
    const ahora = new Date()
    const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
    base = base.filter(m => {
      const fecha = parseFecha(m.fecha_creacion)
      if (!fecha) return false
      if (filtroFecha.value === 'hoy') {
        const fechaSoloFecha = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate())
        return fechaSoloFecha.getTime() === hoy.getTime()
      } else if (filtroFecha.value === 'semana') {
        const hace7 = new Date(hoy); hace7.setDate(hace7.getDate() - 7)
        return fecha >= hace7
      } else if (filtroFecha.value === 'mes') {
        return fecha >= new Date(ahora.getFullYear(), ahora.getMonth(), 1)
      }
      return true
    })
  }

  if (filtroBusqueda.value.trim()) {
    const termino = filtroBusqueda.value.trim().toLowerCase()
    base = base.filter(m => m.nombre?.toLowerCase().includes(termino))
  }

  return [...base].sort((a, b) => {
    const aDesc = a.estado === 'descontinuado' ? 1 : 0
    const bDesc = b.estado === 'descontinuado' ? 1 : 0
    if (aDesc !== bDesc) return aDesc - bDesc
    const fa = parseFecha(a.fecha_creacion)?.getTime() ?? 0
    const fb = parseFecha(b.fecha_creacion)?.getTime() ?? 0
    return fb - fa
  })
})

const modalClonarIdModelo = computed(() =>
  modeloParaClonar.value?.id_modelo ?? versionParaClonar.value?.id_modelo ?? null
)

const modalClonarIdVersionOrigen = computed<number | null>(() => {
  if (modeloParaClonar.value) {
    // Al clonar desde un modelo (sin versión explícita), el backend clona la
    // versión más reciente (mayor id_version). Filtramos el árbol por ESA misma
    // versión para no mezclar indicadores de otras versiones (duplicados).
    const versiones = modeloParaClonar.value.versions ?? []
    if (versiones.length === 0) return null
    return versiones.reduce((max, v) => ((v.id_version ?? 0) > (max.id_version ?? 0) ? v : max)).id_version ?? null
  }
  return versionParaClonar.value?.id_version ?? null
})

// Versiones aplanadas desde modelos filtrados con filtro por estado de versión
const versionesFiltradas = computed<VersionExtendida[]>(() => {
  const ordenEstados: Record<string, number> = {
    completado: 1,
    aprobado: 2,
    edicion: 3,
    descontinuado: 4
  }

  let resultado = modelosFiltrados.value
    .flatMap(m =>
      (m.versions ?? [])
        .filter(v => {
          if (props.modoIndicadores) {
            if (mostrarVersionesProcesosActivos.value) {
              return v.estado === 'edicion' || v.estado === 'aprobado'
            }
            if (v.estado === 'edicion') return !versionesConProceso.value.has(v.id_version!)
            if (versionesTodosFinalizados.value.has(v.id_version!)) return true
            return false
          } else if (props.modoCalificar) {
            return (v.estado === 'aprobado' || v.estado === 'edicion') && versionesEstadoProceso.value[v.id_version!] === 'evaluacion'
          } else if (props.modoEvidencias) {
            return (v.estado === 'aprobado' || v.estado === 'edicion') && versionesEstadoProceso.value[v.id_version!] === 'ejecucion'
          } else if (props.modoAsignarEvaluadores) {
            return (v.estado === 'aprobado' || v.estado === 'edicion') && versionesConProceso.value.has(v.id_version!)
          } else if (props.modoEvaluacionExpertos) {
            return (v.estado === 'aprobado' || v.estado === 'edicion') && versionesEstadoProceso.value[v.id_version!] === 'informe'
          } else {
            return true
          }
        })
        .map(v => ({
          ...v,
          modeloNombre: v.nombre,
          id_tipo_modelo: m.id_tipo_modelo,
          modeloEstado: m.estado ?? ''
          // id_programa ya viene en v desde la versión (C-03)
        }))
    )

  // Filtro por estado
  if (filtroEstado.value) {
    resultado = resultado.filter(v => v.estado === filtroEstado.value)
  }

  // Filtro por fecha
  if (filtroFecha.value) {
    const ahora = new Date()
    const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
    resultado = resultado.filter(v => {
      const fecha = parseFecha(v.fecha_creacion)
      if (!fecha) return false
      
      if (filtroFecha.value === 'hoy') {
        // Comparar solo año, mes y día
        const fechaSoloFecha = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate())
        return fechaSoloFecha.getTime() === hoy.getTime()
      } else if (filtroFecha.value === 'semana') {
        const hace7 = new Date(hoy)
        hace7.setDate(hace7.getDate() - 7)
        return fecha >= hace7
      } else if (filtroFecha.value === 'mes') {
        const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1)
        return fecha >= inicioMes
      }
      return true
    })
  }

  // Filtro por búsqueda de nombre
  if (filtroBusqueda.value.trim()) {
    const termino = filtroBusqueda.value.trim().toLowerCase()
    resultado = resultado.filter(v => v.modeloNombre?.toLowerCase().includes(termino))
  }

  // Filtro por asignación de usuario
  if (props.modoEvidencias || props.modoCalificar || props.modoEvaluacionExpertos) {
    // En estos modos todos los no-admin solo ven sus procesos asignados,
    // independientemente de otros permisos (coordinadores, directores, etc.)
    if (!auth.esAdmin) {
      resultado = resultado.filter(v => {
        const idProceso = versionesIdProceso.value[v.id_version!]
        return idProceso != null && tieneAsignacionProceso(v.id_version!, idProceso)
      })
    }
  } else if (necesitaFiltro.value) {
    // Otros modos: filtro por versión para usuarios sin permisos amplios
    resultado = idsVersiones.value.length > 0
      ? resultado.filter(v => idsVersiones.value.includes(v.id_version!))
      : []
  }

  return resultado.sort((a, b) => {
    if (props.modoIndicadores) {
      const prioV = (e?: string) => e === 'edicion' ? 0 : e === 'descontinuado' ? 2 : 1
      const diff = prioV(a.estado) - prioV(b.estado)
      if (diff !== 0) return diff
    } else {
      const aDesc = a.modeloEstado === 'descontinuado' ? 1 : 0
      const bDesc = b.modeloEstado === 'descontinuado' ? 1 : 0
      if (aDesc !== bDesc) return aDesc - bDesc
    }
    const fechaA = parseFecha(a.fecha_creacion)?.getTime() ?? 0
    const fechaB = parseFecha(b.fecha_creacion)?.getTime() ?? 0
    return fechaB - fechaA
  })
})

const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

// ── Paginación ──────────────────────────────────────────────
const ITEMS_POR_PAGINA = 9
const paginaActual = ref(1)

const totalPaginas = computed(() => {
  const total = modoNormal.value ? modelosParaCardsNormal.value.length : versionesFiltradas.value.length
  return Math.max(1, Math.ceil(total / ITEMS_POR_PAGINA))
})

const modelosPaginados = computed<ModeloConVersiones[]>(() => {
  const inicio = (paginaActual.value - 1) * ITEMS_POR_PAGINA
  return modelosParaCardsNormal.value.slice(inicio, inicio + ITEMS_POR_PAGINA)
})

const versionesPaginadas = computed<VersionExtendida[]>(() => {
  const inicio = (paginaActual.value - 1) * ITEMS_POR_PAGINA
  return versionesFiltradas.value.slice(inicio, inicio + ITEMS_POR_PAGINA)
})

watch(
  [filtroTipo, filtroPrograma, filtroEstado, filtroFecha, filtroBusqueda, mostrarVersionesProcesosActivos],
  () => { paginaActual.value = 1 }
)

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

// ── Editar nombre de versión ────────────────────────────────
const editarVersionVisible = ref(false)
const versionAEditar = ref<VersionExtendida | null>(null)
const nombreEdicion = ref('')
const guardandoEdicion = ref(false)

function abrirEditarVersion(v: VersionExtendida) {
  versionAEditar.value = v
  nombreEdicion.value = v.nombre ?? ''
  editarVersionVisible.value = true
}

async function guardarEdicionVersion() {
  const v = versionAEditar.value
  if (!v?.id_version || !nombreEdicion.value.trim() || guardandoEdicion.value) return
  guardandoEdicion.value = true
  try {
    const ok = await versionesStore.updateVersion({ ...v, nombre: nombreEdicion.value.trim() })
    if (ok) {
      mostrarMensaje('Nombre actualizado correctamente.', 'positivo')
      editarVersionVisible.value = false
      versionAEditar.value = null
      await cargar()
    } else {
      mostrarMensaje('No se pudo actualizar el nombre.', 'error')
    }
  } finally {
    guardandoEdicion.value = false
  }
}

function formatFecha(fecha?: string | null) {
  if (!fecha) return '-'
  const soloFecha = fecha.substring(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(soloFecha)) {
    const [yyyy, mm, dd] = soloFecha.split('-').map(Number)
    const d = new Date(yyyy, mm - 1, dd)
    return formatterFecha.format(d)
  }
  const d = new Date(fecha)
  return isNaN(d.getTime()) ? fecha : formatterFecha.format(d)
}

const ETIQUETAS_ESTADO_PROCESO: Record<string, string> = {
  preparacion: 'Preparación',
  ejecucion: 'Ejecución',
  evaluacion: 'Evaluación',
  informe: 'Informe',
  finalizado: 'Finalizado'
}
function etiquetaEstadoProceso(estado?: string): string {
  return ETIQUETAS_ESTADO_PROCESO[estado ?? ''] ?? estado ?? 'Activo'
}

function estadoClass(estado?: string | null) {
  switch (estado) {
    case 'aprobado': return 'estado-aprobado'
    case 'descontinuado': return 'estado-descontinuado'
    case 'completado': return 'estado-completado'
    default: return 'estado-modificar'
  }
}

function tipoDeModelo(v: VersionExtendida) {
  return tipoModeloStore.tipos.find(t => t.id_tipo_modelo === v.id_tipo_modelo) ?? null
}

function tipoDeModeloById(idTipo?: number | null) {
  if (!idTipo) return null
  return tipoModeloStore.tipos.find(t => t.id_tipo_modelo === idTipo) ?? null
}

function versionActivaDeModelo(m: ModeloConVersiones): Version | null {
  return (m.versions ?? []).find(vv => vv.estado !== 'descontinuado') ?? null
}

function accederModelo(m: ModeloConVersiones) {
  if (!m.id_modelo) return

  if (m.estado === 'edicion') {
    router.push({ name: 'EstructuraModelo', params: { id_modelo: m.id_modelo } })
    return
  }

  const activa = versionActivaDeModelo(m)

  if (!activa) {
    // Modelo descontinuado: muestra árbol de componentes y pesos, sin requerir versión.
    router.push({ name: 'PresentarDocumento', params: { id_modelo: m.id_modelo, id_version: 0 } })
    return
  }

  if (activa.estado === 'aprobado') {
    router.push({ name: 'PresentarDocumento', params: { id_modelo: m.id_modelo, id_version: activa.id_version } })
  } else if (activa.estado === 'completado') {
    router.push({ name: 'ModeloCompleto', params: { id_modelo: m.id_modelo, id_version: activa.id_version } })
  }
}

function abrirModalVersionarModelo(m: ModeloConVersiones) {
  if (versionando.value !== null) return
  modeloParaVersionar.value = m
  versionParaVersionar.value = null
  modalVersionarVisible.value = true
}

function abrirModalClonarModelo(m: ModeloConVersiones) {
  if (!m.id_modelo || clonando.value !== null) return
  modeloParaClonar.value = m
  versionParaClonar.value = null
  modalClonarVisible.value = true
}

function toggleCard(id: number) {
  cardSeleccionada.value = cardSeleccionada.value === id ? null : id
}

async function cargar() {
  loading.value = true
  error.value = null
  try {
    const tareas: Promise<any>[] = [
      modeloStore.getModelo().then(m => { modelos.value = m as ModeloConVersiones[] }),
      tipoModeloStore.getTipoModelos(),
      programaStore.cargarTodos(),
      campusStore.cargarTodos()
    ]

    if (!modoNormal.value) {
      tareas.push(
        procesosStore.cargarTodos().then(() => {
          const todos = procesosStore.procesos
          const activos = todos.filter(p => p.estado !== 'finalizado')
          versionesConProceso.value = new Set(activos.map(p => p.id_version).filter((id): id is number => id != null))

          const todasVersionesEnProceso = new Set(todos.map(p => p.id_version).filter((id): id is number => id != null))
          const finalizados = new Set<number>()
          for (const idV of todasVersionesEnProceso) {
            if (todos.filter(p => p.id_version === idV).every(p => p.estado === 'finalizado')) {
              finalizados.add(idV)
            }
          }
          versionesTodosFinalizados.value = finalizados
          const estadoMap: Record<number, string> = {}
          const idMap: Record<number, number> = {}
          for (const p of activos) {
            estadoMap[p.id_version] = p.estado ?? ''
            idMap[p.id_version] = p.id!
          }
          versionesEstadoProceso.value = estadoMap
          versionesIdProceso.value = idMap
        })
      )
    }

    await Promise.all(tareas)
  } catch (err: any) {
    error.value = err?.message || 'Error desconocido'
  } finally {
    loading.value = false
  }
}

// Navegación de los botones
function accederVersion(v: VersionExtendida) {
  if (!v.id_modelo || !v.id_version) return
  if (v.estado === 'aprobado') {
    router.push({ name: 'PresentarDocumento', params: { id_modelo: v.id_modelo, id_version: v.id_version } })
  } else if (v.estado === 'completado') {
    router.push({ name: 'ModeloCompleto', params: { id_modelo: v.id_modelo, id_version: v.id_version } })
  } else {
    router.push({ name: 'EstructuraModelo', params: { id_modelo: v.id_modelo, id_version: v.id_version } })
  }
}

function agregarIndicadoresVersion(v: VersionExtendida) {
  if (!v.id_version) return
  router.push({ name: 'AgregarIndicadores', params: { id_version: v.id_version } })
}

function calificarInstrumentosVersion(v: VersionExtendida) {
  if (!v.id_version) return
  router.push({ name: 'ListaCalificar', params: { id_version: v.id_version } })
}

function subirEvidenciasVersion(v: VersionExtendida) {
  if (!v.id_version) return
  router.push({ name: 'EvidenciasInstrumentos', params: { id_version: v.id_version } })
}

function navegarEvaluacionExpertos(v: VersionExtendida, routeName: string) {
  if (!v.id_version) return
  router.push({ name: routeName, params: { id_version: v.id_version } })
}

//clonar versiones
async function clonarVersion(v: VersionExtendida) {
  if (!v.id_version) return
  if (clonando.value !== null) return

  clonando.value = v.id_version
  try {
    const nuevaVersion = await versionesStore.clonarVersion(v.id_version)
    if (!nuevaVersion) {
      mostrarMensaje('No se pudo clonar la versión', 'error')
      return
    }

    mostrarMensaje(`Versión clonada correctamente: ${nuevaVersion.nombre}`, 'positivo')

    await cargar()
  } catch (err) {
    console.error('Error al clonar versión:', err)
    mostrarMensaje('Ocurrió un error al clonar la versión', 'error')
  } finally {
    clonando.value = null
  }
}

function abrirModalVersionar(v: VersionExtendida) {
  if (!v.id_version || versionando.value !== null) return
  versionParaVersionar.value = v
  modalVersionarVisible.value = true
}

async function confirmarVersionar(payload: { nuevoNombre?: string; enfoque?: string }) {
  modalVersionarVisible.value = false

  const nuevoNombre = payload.nuevoNombre
  // Enfoque elegido para la nueva versión ('general' = sin filtro, se omite).
  const enfoque = payload.enfoque && payload.enfoque !== 'general' ? payload.enfoque : undefined

  const m = modeloParaVersionar.value
  const v = versionParaVersionar.value

  if (m) {
    const idModelo = m.id_modelo!
    versionando.value = idModelo
    try {
      const nueva = await versionesStore.crearVersion({
        id_modelo:        idModelo,
        nombre:           nuevoNombre ?? m.nombre,
        estado:           'edicion',
        fecha_creacion:   new Date().toISOString().split('T')[0],
        fecha_aprobacion: null,
        ambito:           null,
        id_programa:      null,
        enfoque:          enfoque ?? null
      })
      if (!nueva) { mostrarMensaje('No se pudo crear la nueva versión.', 'error'); return }
      mostrarMensaje(`Versión creada: ${nueva.nombre}`, 'positivo')
      await cargar()
      router.push({ name: 'ListaVersiones', query: { modo: 'indicadores' } })
    } finally {
      modeloParaVersionar.value = null
      versionando.value = null
    }
  } else if (v?.id_version) {
    versionando.value = v.id_version
    try {
      const nueva = await versionesStore.clonarVersion(v.id_version, nuevoNombre, undefined, enfoque)
      if (!nueva) { mostrarMensaje('No se pudo crear la nueva versión.', 'error'); return }
      mostrarMensaje(`Nueva versión creada: ${nueva.nombre}`, 'positivo')
      await cargar()
      router.push({ name: 'ListaVersiones', query: { modo: 'indicadores' } })
    } finally {
      versionParaVersionar.value = null
      versionando.value = null
    }
  }
}

// Abrir modal de clonación — siempre clona el modelo (no la versión)
function abrirModalClonar(v: VersionExtendida) {
  if (!v.id_modelo || !v.id_version || clonando.value !== null) return
  versionParaClonar.value = v
  modalClonarVisible.value = true
}

async function confirmarClonarModelo(payload: { opcion: string; ordinales?: string[]; nuevoNombre?: string }) {
  if (clonarEnProceso.value) {
    console.warn('Clonación ya en proceso, ignorando evento')
    return
  }

  clonarEnProceso.value = true
  modalClonarVisible.value = false

  const idModelo = modeloParaClonar.value?.id_modelo ?? versionParaClonar.value?.id_modelo
  if (!idModelo) {
    clonarEnProceso.value = false
    return
  }

  try {
    const ordinales = payload.opcion === 'seleccion' && (payload.ordinales?.length ?? 0) > 0
      ? payload.ordinales
      : undefined
    await ejecutarClonarModelo(idModelo, ordinales, payload.nuevoNombre)
  } finally {
    modeloParaClonar.value = null
    versionParaClonar.value = null
    clonarEnProceso.value = false
  }
}

// C-07: clonar modelo, opcionalmente con filtro de ordinales para clonación parcial.
// El clon copia SOLO la estructura: no nace con versión ni hereda indicadores.
async function ejecutarClonarModelo(id_modelo: number, ordinales?: string[], nuevoNombre?: string) {
  clonando.value = id_modelo as any
  const payload = {
    ...(ordinales?.length ? { ordinales } : {}),
    ...(nuevoNombre ? { nuevo_nombre: nuevoNombre } : {})
  }
  try {
    const clonado = await modeloStore.clonarModelo(id_modelo, payload)
    if (!clonado) {
      mostrarMensaje('No se pudo clonar el modelo', 'error')
      return
    }
    mostrarMensaje(`Modelo clonado correctamente: ${clonado.nombre}`, 'positivo')
    await cargar()
  } catch (err) {
    console.error('Error al clonar modelo:', err)
    mostrarMensaje('Ocurrió un error al clonar el modelo', 'error')
  } finally {
    clonando.value = null
  }
}

// ── Confirmación descontinuar ───────────────────────────────
const confirmarDescontinuarVisible = ref(false)
const confirmarDescontinuarTexto = ref('')
const confirmarDescontinuarNombre = ref('')
type DescontinuarPendiente = { tipo: 'modelo'; id: number } | { tipo: 'version'; v: VersionExtendida }
let _descontinuarPendiente: DescontinuarPendiente | null = null

function abrirConfirmarDescontinuarModelo(m: { id_modelo?: number; nombre?: string }) {
  if (!m.id_modelo) return
  _descontinuarPendiente = { tipo: 'modelo', id: m.id_modelo }
  confirmarDescontinuarNombre.value = m.nombre ?? ''
  confirmarDescontinuarTexto.value = ''
  confirmarDescontinuarVisible.value = true
}

function abrirConfirmarDescontinuarVersion(v: VersionExtendida) {
  if (!v.id_version) return
  _descontinuarPendiente = { tipo: 'version', v }
  confirmarDescontinuarNombre.value = v.nombre ?? ''
  confirmarDescontinuarTexto.value = ''
  confirmarDescontinuarVisible.value = true
}

function cerrarConfirmarDescontinuar() {
  confirmarDescontinuarVisible.value = false
  confirmarDescontinuarTexto.value = ''
  _descontinuarPendiente = null
}

async function ejecutarDescontinuar() {
  if (confirmarDescontinuarTexto.value.trim().toLowerCase() !== 'descontinuar') return
  if (!_descontinuarPendiente) return
  const pendiente = _descontinuarPendiente
  cerrarConfirmarDescontinuar()
  if (pendiente.tipo === 'modelo') {
    await descontinuarModelo(pendiente.id)
  } else {
    await descontinuarVersion(pendiente.v)
  }
}

async function descontinuarModelo(id_modelo?: number) {
  if (!id_modelo) return
  descontinuando.value = true
  try {
    const result = await modeloStore.descontinuarModelo(id_modelo)
    if (result.ok) {
      mostrarMensaje('El modelo fue descontinuado correctamente.', 'positivo')
      await cargar()
    } else {
      mostrarMensaje(result.message ?? 'No se pudo descontinuar el modelo.', 'error')
    }
  } finally {
    descontinuando.value = false
  }
}

async function descontinuarVersion(v: VersionExtendida): Promise<void> {
  if (!v.id_version) return
  descontinuando.value = true
  try {
    const ok = await versionesStore.cambiarEstadoVersion(v.id_version, 'descontinuado')
    if (ok) {
      mostrarMensaje('Versión descontinuada correctamente.', 'positivo')
      await cargar()
    } else {
      mostrarMensaje('No se pudo descontinuar la versión.', 'error')
    }
  } catch {
    mostrarMensaje('Error al descontinuar la versión.', 'error')
  } finally {
    descontinuando.value = false
  }
}

async function completarEtapa(v: VersionExtendida) {
  const idProceso = versionesIdProceso.value[v.id_version!]
  if (!idProceso) {
    mostrarMensaje('No se encontró el proceso asociado a esta versión.', 'error')
    return
  }
  const nuevoEstado = props.modoEvidencias ? 'evaluacion' : 'informe'
  completandoEtapa.value = true
  try {
    const result = await procesosStore.cambiarEstado(idProceso, nuevoEstado as any)
    if (result.ok) {
      mostrarMensaje('Etapa completada correctamente.', 'positivo')
      cardSeleccionada.value = null
      await cargar()
    } else {
      mostrarMensaje('No se pudo completar la etapa.', 'error')
    }
  } finally {
    completandoEtapa.value = false
  }
}

onMounted(cargar)
</script>

<style scoped>
.contenedor-tabla { 
  width: 100%; 
  overflow-x: auto; 
  -webkit-overflow-scrolling: touch;
}

.loading { 
  padding: 1rem; 
  color: #333; 
}

.error { 
  padding: 1rem; 
  color: #b00020; 
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
  padding: 0.75rem;
}

.modelo-card {
  background-color: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.modelo-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
}

.modelo-card.card-activa {
  border-color: #0e69a0;
  border-width: 2px;
  box-shadow: 0 4px 12px rgba(14, 105, 160, 0.15);
}

.estado-badge {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.card-titulo {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
  letter-spacing: 0.01em;
}

.card-info {
  font-size: 0.9rem;
  color: #555;
  margin: 0 0 0.5rem 0;
}

.card-acciones {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.estado {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.estado-modificar {
  background-color: #fef0c7;
  color: #92550a;
}

.estado-aprobado { 
  background-color: #d1f3d1; 
  color: #1a7f1a; 
}

.estado-completado { 
  background-color: #0e69a0; 
  color: white;         
}

.estado-descontinuado {
  background-color: #e0e0e0;
  color: #666;
}

/* Botones */
.btn {
  padding: 0.5rem 0.9rem;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.guardar { 
  background-color: #eea222; 
  color: black; 
}


.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

.btn.completar {
  background-color: #eea222;
  color: black;
}

.btn.completar:hover:not(:disabled) {
  background-color: #0e69a0;
  color: white;
}

.btn.salir { background: #fff; border: 1px solid #ccc; color: #333; }
.btn.salir:hover:not(:disabled) { background: #e8e8e8; }

.btn.peligro { background: #fff; border: 1px solid #c0392b; color: #c0392b; }
.btn.peligro:hover:not(:disabled) { background: #c0392b; color: #fff; }
.btn.peligro:disabled { opacity: 0.5; cursor: not-allowed; }

.badge-proceso-activo {
  display: inline-block;
  margin-top: 0.4rem;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  background: #d4edda;
  color: #155724;
  text-transform: capitalize;
}

/* Filtros */
.filtro-botones {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap; 
}

.btn-filtro {
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-filtro:hover {
  background-color: #eea222;
  color: white;
}

.btn-filtro.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.filtro-select {
  margin-bottom: 1rem;
}

.filtro-select label {
  margin-right: 0.5rem;
  font-weight: 600;
  color: #333;
}

.filtro-select select {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filtro-select select:hover {
  border-color: #0e69a0;
}

/* Filtros secundarios */
.filtros-secundarios {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: center;
}

.filtro-grupo {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.filtro-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.filtro-botones-sm {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.select-filtro {
  padding: 0.35rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  font-size: 0.85rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}

.select-filtro:hover {
  border-color: #0e69a0;
}

.select-filtro:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.btn-filtro-sm {
  padding: 0.3rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  cursor: pointer;
  font-size: 0.82rem;
  transition: all 0.2s ease;
}

.btn-filtro-sm:hover {
  background-color: #eea222;
  color: white;
}

.btn-filtro-sm.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.input-busqueda {
  padding: 0.35rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  width: 180px;
}

.input-busqueda:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  user-select: none;
}

.toggle-check {
  accent-color: #0e69a0;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

/* ── Paginación ── */
.paginacion {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 0.75rem 0.5rem;
}

.btn-pag {
  padding: 0.4rem 1.1rem;
  border: 1px solid #ccc;
  border-radius: 20px;
  background: #f9f9f9;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  font-family: inherit;
}

.btn-pag:hover:not(:disabled) {
  background: #0e69a0;
  border-color: #0e69a0;
  color: white;
}

.btn-pag:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Modal editar versión ── */
.ev-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.ev-panel {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  width: 420px;
  max-width: 90vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  font-family: var(--font-family);
}

.ev-titulo {
  margin: 0 0 1.25rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
}

.ev-campo {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
}

.ev-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
}

.ev-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: var(--font-family);
  background: #f9f9f9;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.ev-input:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.ev-botones {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.dc-aviso {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  color: #444;
  line-height: 1.5;
}

.dc-objetivo {
  margin: 0 0 1rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  padding: 4px 10px;
  background: #f5f5f5;
  border-left: 3px solid #c0392b;
  border-radius: 3px;
}

.pag-info {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  min-width: 70px;
  text-align: center;
}

@media (max-width: 600px) {
  .cards-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0.5rem;
  }

  .filtros-secundarios {
    flex-direction: column;
    align-items: flex-start;
  }

  .input-busqueda {
    width: 100%;
  }
}
</style>